import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { formatPercentage, formatNumber, displayPercentage } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const InvestmentGrowthCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  const [storedFormData, setStoredFormData] = useState()
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      initialBalance: 0,
      annualSavings: 0,
      years: 20,
      btReturnTaxable: 8,
      btReturnDeferred: 8,
      btReturnFree: 5,
      taxBracket: 25,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const numberValidationMessage = t('InvestmentGrowthCalculator.numberRangeMessage', {
    defaultValue: 'Please enter a number between -12 and 12',
  })
  const validationYearMessage = t('InvestmentGrowthCalculator.YearMessage', {
    defaultValue: 'Please enter a number between 1 and 50',
  })
  const taxBracketMessage = t('InvestmentGrowthCalculator.taxBracketMessage', {
    defaultValue: 'Please enter a number between 0 and 75',
  })

  const validationSchema = {
    initialBalance: {
      min: 0,
      max: 999999999,
    },
    annualSavings: {
      min: 0,
      max: 999999999,
    },
    years: {
      min: 1,
      max: 50,
      validate: value => (value > 1 && value <= 50) || validationYearMessage,
    },
    btReturnTaxable: {
      min: -12,
      max: 12,
      validate: value => (value >= -12 && value <= 12) || numberValidationMessage,
    },
    btReturnDeferred: {
      min: -12,
      max: 12,
      validate: value => (value >= -12 && value <= 12) || numberValidationMessage,
    },
    btReturnFree: {
      min: -12,
      max: 12,
      validate: value => (value >= -12 && value <= 12) || numberValidationMessage,
    },
    taxBracket: {
      min: 0,
      max: 75,
      validate: value => (value >= 0 && value <= 75) || taxBracketMessage,
    },
  }

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)
    setStoredFormData(formData)

    try {
      const url = 'https://www.calcxml.com/rest/inc07'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Inc07JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        initialBalance: formData.initialBalance,
        annualSavings: formData.annualSavings,
        years: formData.years,
        btReturnTaxable: formatPercentage(formData.btReturnTaxable),
        btReturnDeferred: formatPercentage(formData.btReturnDeferred),
        btReturnFree: formatPercentage(formData.btReturnFree),
        taxBracket: formatPercentage(formData.taxBracket),
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('InvestmentGrowthCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('InvestmentGrowthCalculator.firstLegend', {
              defaultValue: 'Savings',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.initialBalanceLabel', {
                  defaultValue: 'Current investment balance ($)',
                })}
                id="initialBalance"
                name="initialBalance"
                type="number"
                ref={register(validationSchema.initialBalance)}
                error={formatError(errors.initialBalance, validationSchema.initialBalance, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.annualSavingsLabel', {
                  defaultValue: 'Annual contributions ($)',
                })}
                id="annualSavings"
                name="annualSavings"
                type="number"
                ref={register(validationSchema.annualSavings)}
                error={formatError(errors.annualSavings, validationSchema.annualSavings, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.numberOfyearsLabel', {
                  defaultValue: 'Number of years to invest (1 to 50)',
                })}
                id="years"
                name="years"
                type="number"
                min="1"
                max="50"
                ref={register(validationSchema.years)}
                error={formatError(errors.years, validationSchema.years, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('InvestmentGrowthCalculator.secondLegend', {
              defaultValue: 'Assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.btReturnTaxableLabel', {
                  defaultValue: 'Before-tax return on fully-taxable investment (-12% to 12%)',
                })}
                id="btReturnTaxable"
                name="btReturnTaxable"
                type="number"
                min="-12"
                max="12"
                ref={register(validationSchema.btReturnTaxable)}
                error={formatError(errors.btReturnTaxable, validationSchema.btReturnTaxable, t)}
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.btReturnDeferredLabel', {
                  defaultValue: 'Before-tax return on tax-deferred investment (-12% to 12%)',
                })}
                id="btReturnDeferred"
                name="btReturnDeferred"
                type="number"
                min="-12"
                max="12"
                ref={register(validationSchema.btReturnDeferred)}
                error={formatError(errors.btReturnDeferred, validationSchema.btReturnDeferred, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.btReturnFreeLabel', {
                  defaultValue: 'Return on tax-free investment (-12% to 12%)',
                })}
                id="btReturnFree"
                name="btReturnFree"
                type="number"
                min="-12"
                max="12"
                ref={register(validationSchema.btReturnFree)}
                error={formatError(errors.btReturnFree, validationSchema.btReturnFree, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('InvestmentGrowthCalculator.taxBracketLabel', {
                  defaultValue: 'Marginal tax bracket (0% to 75%)',
                })}
                id="taxBracket"
                name="taxBracket"
                type="number"
                min="0"
                max="75"
                ref={register(validationSchema.taxBracket)}
                error={formatError(errors.taxBracket, validationSchema.taxBracket, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('InvestmentGrowthCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('InvestmentGrowthCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('InvestmentGrowthCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('InvestmentGrowthCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('InvestmentGrowthCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>
          <p>{calcResults.responseText2}</p>
          <p>{calcResults.responseText3}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('InvestmentGrowthCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('InvestmentGrowthCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th className="section">
                  {t('InvestmentGrowthCalculator.summary', {
                    defaultValue: 'Summary',
                  })}
                </th>
                <th scope="col">
                  {t('InvestmentGrowthCalculator.fullyTaxable', {
                    defaultValue: 'Fully-taxable',
                  })}
                </th>
                <th scope="col">
                  {t('InvestmentGrowthCalculator.taxDeferred', {
                    defaultValue: 'Tax-deferred',
                  })}
                </th>
                <th scope="col">
                  {t('InvestmentGrowthCalculator.taxFree', {
                    defaultValue: 'Tax-free',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.currentInvestmentBalance', {
                    defaultValue: 'Current investment balance',
                  })}
                </th>
                <td>${formatNumber(storedFormData.initialBalance)}</td>
                <td>${formatNumber(storedFormData.initialBalance)}</td>
                <td>${formatNumber(storedFormData.initialBalance)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.annualContributions', {
                    defaultValue: 'Annual contributions',
                  })}
                </th>
                <td>${formatNumber(storedFormData.annualSavings)}</td>
                <td>${formatNumber(storedFormData.annualSavings)}</td>
                <td>${formatNumber(storedFormData.annualSavings)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.numberYearsToInvest', {
                    defaultValue: 'Number of years to invest',
                  })}
                </th>
                <td>{storedFormData.years}</td>
                <td>{storedFormData.years}</td>
                <td>{storedFormData.years}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.beforeTaxReturn', {
                    defaultValue: 'Before-tax return',
                  })}
                </th>
                <td>{storedFormData.btReturnTaxable}%</td>
                <td>{storedFormData.btReturnDeferred}%</td>
                <td>{storedFormData.btReturnFree}%</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.marginalTaxBracket', {
                    defaultValue: 'Marginal tax bracket',
                  })}
                </th>
                <td>{storedFormData.taxBracket}%</td>
                <td>{storedFormData.taxBracket}%</td>
                <td>{storedFormData.taxBracket}%</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.afterTaxReturn', {
                    defaultValue: 'After-tax return',
                  })}
                </th>
                <td>{displayPercentage(calcResults.atReturnTaxable)}%</td>
                <td>{storedFormData.btReturnDeferred}%</td>
                <td>{storedFormData.btReturnFree}%</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.futureAccountValue', {
                    defaultValue: 'Future account value *',
                  })}
                </th>
                <td>{calcResults.fvTaxable}</td>
                <td>{calcResults.fvDeferred}</td>
                <td>{calcResults.fvFree}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('InvestmentGrowthCalculator.futureAccountAfterTax', {
                    defaultValue: 'Future account value (after-tax)',
                  })}
                </th>
                <td>{calcResults.fvatTaxable}</td>
                <td>{calcResults.fvatDeferred}</td>
                <td>{calcResults.fvatFree}</td>
              </tr>
              <tr>
                <td colSpan="4">
                  {t('InvestmentGrowthCalculator.lumpSumMessage', {
                    defaultValue: `* The lump sum shown after taxes is based on the marginal tax rate that you
                    selected. This lump sum after tax figure does not take into account the possible
                    change in tax bracket that might occur due to a lump sum distribution of the
                    taxable amount, nor does it take into effect any applicable tax penalties.`,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('InvestmentGrowthCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('InvestmentGrowthCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { InvestmentGrowthCalculator }
export default InvestmentGrowthCalculator
